import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ShoppingBag, Tag, BookOpen, Package } from "lucide-react";

function timeAgo(ts) {
  if (!ts) return "";
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 7 * 86400) return `${Math.floor(secs / 86400)}d ago`;
  return new Date(ts).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function TransactionHistory() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bought");
  const [bought, setBought] = useState([]);
  const [sold, setSold] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    async function load() {
      const snap = await get(ref(db, "listings"));
      if (!snap.exists()) { setLoading(false); return; }
      const all = Object.entries(snap.val()).map(([id, d]) => ({ id, ...d }));
      setBought(all.filter(l => l.soldTo === currentUser.uid).sort((a, b) => b.createdAt - a.createdAt));
      setSold(all.filter(l => l.sellerId === currentUser.uid && l.status === "sold").sort((a, b) => b.createdAt - a.createdAt));
      setLoading(false);
    }
    load();
  }, [currentUser]);

  const items = tab === "bought" ? bought : sold;

  if (!currentUser) return null;

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-white border-b px-4 py-4 sticky top-14 z-10 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-full hover:bg-gray-100">
          <ArrowLeft size={20} className="text-gray-700" />
        </button>
        <h2 className="text-lg font-bold text-gray-800">Transaction History</h2>
      </div>

      {/* Summary cards */}
      <div className="px-4 pt-4 grid grid-cols-2 gap-3">
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 text-center">
          <ShoppingBag size={22} className="mx-auto text-blue-500 mb-1" />
          <p className="text-2xl font-bold text-blue-700">{bought.length}</p>
          <p className="text-xs text-blue-500 font-medium">Items Bought</p>
        </div>
        <div className="bg-green-50 border border-green-100 rounded-2xl p-4 text-center">
          <Tag size={22} className="mx-auto text-green-500 mb-1" />
          <p className="text-2xl font-bold text-green-700">{sold.length}</p>
          <p className="text-xs text-green-500 font-medium">Items Sold</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 pt-4 flex gap-2">
        <button onClick={() => setTab("bought")} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition ${tab === "bought" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"}`}>
          🛍️ Bought ({bought.length})
        </button>
        <button onClick={() => setTab("sold")} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition ${tab === "sold" ? "bg-green-600 text-white border-green-600" : "bg-white text-gray-600 border-gray-200"}`}>
          💰 Sold ({sold.length})
        </button>
      </div>

      <div className="px-4 pt-3 space-y-2 max-w-lg mx-auto">
        {loading ? (
          [1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-xl animate-pulse" />)
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <Package size={48} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-500 font-semibold">{tab === "bought" ? "No purchases yet" : "No sales yet"}</p>
            <p className="text-gray-400 text-sm mt-1">{tab === "bought" ? "Items you buy will appear here" : "Items you sell will appear here"}</p>
            {tab === "bought" && <Link to="/browse" className="mt-4 inline-block bg-blue-600 text-white px-5 py-2 rounded-xl text-sm font-semibold">Browse Listings</Link>}
          </div>
        ) : items.map(l => (
          <Link key={l.id} to={`/listing/${l.id}`}
            className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3 hover:border-blue-100 hover:shadow-sm transition">
            <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
              {l.imageUrl || (l.images && l.images[0])
                ? <img src={l.imageUrl || l.images[0]} alt={l.title} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center"><BookOpen size={18} className="text-gray-300" /></div>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm line-clamp-1">{l.title}</p>
              <p className="text-blue-600 font-bold text-sm">₹{l.price}</p>
              <p className="text-gray-400 text-xs mt-0.5">{timeAgo(l.createdAt)} · {l.college}</p>
            </div>
            <div className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-bold ${tab === "bought" ? "bg-blue-50 text-blue-600" : "bg-green-50 text-green-600"}`}>
              {tab === "bought" ? "Bought" : "Sold"}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
